<template>
    <div v-if="visibleUser" :key="visibleUser._id">
        <div class="gradient-box gradient-box-peach"></div>
        <div class="row">
            <aside class="col-12 col-lg-2" data-test-id="sidebar">
                <div class="align-with-content tw-text-center tw-ml-4 xl:tw-text-left">
                    <a
                        :href="isVisitor ? '' : `#/profile/${currentUser._id}/edit`"
                        class="tw-inline-block"
                        :class="{ 'tw-cursor-default': isVisitor }"
                        @click="
                            e => {
                                if (isVisitor) e.preventDefault();
                            }
                        "
                    >
                        <l-profile-picture
                            :user="visibleUser"
                            size="xl"
                            class="tw-inline-block"
                            img-classes="tw-ring-2 tw-ring-white"
                            data-test-id="profile-picture"
                        />
                    </a>

                    <h2
                        :title="visibleUser.fullname"
                        class="tw-block tw-mt-4 tw-mb-0 tw-text-xl tw-font-bold tw-leading-tight"
                    >
                        <span class="tw-truncate tw-inline-block tw-max-w-full">{{
                            visibleUser.firstname
                        }}</span>
                        <br />
                        <span class="tw-truncate tw-inline-block tw-max-w-full">{{
                            visibleUser.lastname
                        }}</span>
                    </h2>

                    <div v-if="visibleUser.teamRole" class="tw-text-gray-500 tw-truncate">
                        {{ visibleUser.teamRole.title }}
                    </div>

                    <div v-if="visibleUser.pronouns" class="tw-text-gray-500 tw-leading-none">
                        {{ visibleUser.pronouns }}
                    </div>
                    <l-space v-else :size="1" />

                    <l-user-account-direct-colleagues
                        class="tw-hidden xl:tw-block tw-text-left tw-mt-6"
                        :user-id="$route.params.userId"
                    />
                </div>
            </aside>

            <!-- profile content -->

            <div v-if="!isLoading && !isTeamDashboard" class="col-12 col-lg-10">
                <l-card-content data-test-id="profile-notifications-section">
                    <l-notifications-preview-visitor
                        v-if="isVisitor"
                        :user="userDetails($route.params.userId)"
                    />
                    <l-notifications-preview v-else :user="currentUser" />
                </l-card-content>

                <l-card-content
                    v-if="currentUser.rights.G_VIEW"
                    data-test-id="profile-goals-section"
                >
                    <l-goals-preview-visitor
                        v-if="isVisitor"
                        :user="userDetails($route.params.userId)"
                    />
                    <l-goals-preview v-else :user="currentUser" />
                </l-card-content>

                <l-card-content
                    v-if="currentUser.rights.M_VIEW"
                    data-test-id="profile-meetings-section"
                >
                    <l-meetings-preview
                        v-if="!isVisitor"
                        :user="currentUser"
                        :is-visitor="isVisitor"
                    />
                    <l-meetings-preview v-else :user="visibleUser" :is-visitor="isVisitor" />
                </l-card-content>

                <l-card-content
                    v-if="isVisitor || currentUser.rights.F_VIEW"
                    data-test-id="profile-feedback-section"
                >
                    <l-instant-feedback-preview :is-visitor="isVisitor" :user="visibleUser" />
                </l-card-content>

                <l-card-content
                    v-if="currentUser.rights.F_STRUCTURED || currentUser.rights.R_VIEW"
                    data-test-id="profile-feedback-reports-section"
                >
                    <l-feedback-reports-preview :is-visitor="isVisitor" :user="visibleUser" />
                </l-card-content>

                <l-card-content
                    v-if="!isVisitor && learningPaths.length && currentUser.rights.L_VIEW"
                    data-test-id="profile-learning-section"
                >
                    <l-learning-paths-preview :user="currentUser" />
                </l-card-content>

                <l-card-content v-if="fileAttachments.length" data-test-id="profile-files-section">
                    <l-file-attachments-preview :attachments="fileAttachments" />
                </l-card-content>
            </div>

            <!-- team dashboard content -->

            <div v-if="!isLoading && isTeamDashboard" class="col-12 col-lg-10">
                <l-card-content>
                    <l-team-dashboard :is-visitor="isVisitor" :user="visibleUser" />
                </l-card-content>
            </div>

            <l-modal
                v-if="showModal"
                size="m"
                data-test-id="profile-page-modal"
                :close-on-backdrop-click="false"
                @close="goBack"
            >
                <template #header>
                    <i
                        v-if="showPrivateNoteLock"
                        class="fal fa-lock r-default-margin-s"
                        data-test-id="lock-private-note"
                    ></i>
                    {{ modalTitle }}
                </template>
                <template #body>
                    <router-view @close="goBack" @title="updateModalTitle"></router-view>
                </template>
            </l-modal>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import LCardContent from "@/components/LCardContent";
import LNotificationsPreview from "@/components/LUserProfile/LNotificationsPreview";
import LNotificationsPreviewVisitor from "@/components/LUserProfile/LNotificationsPreviewVisitor";
import LGoalsPreview from "@/components/LUserProfile/LGoalsPreview";
import LGoalsPreviewVisitor from "@/components/LUserProfile/LGoalsPreviewVisitor";
import LMeetingsPreview from "@/components/LUserProfile/LMeetingsPreview";
import LLearningPathsPreview from "@/components/LUserProfile/LLearningPathsPreview";
import LFeedbackReportsPreview from "@/components/LUserProfile/LFeedbackReportsPreview";
import LInstantFeedbackPreview from "@/components/LUserProfile/LInstantFeedbackPreview";
import LFileAttachmentsPreview from "@/components/LUserProfile/LFileAttachmentsPreview";
import LModal from "@/components/LModal";
import LProfilePicture from "@/components/LProfilePicture";
import LUserAccountDirectColleagues from "@/components/LUserAccountDirectColleagues";
import LSpace from "@/components/LSpace";
import LTeamDashboard from "@/components/LUserProfile/LTeamDashboard.vue";

export default {
    components: {
        LCardContent,
        LNotificationsPreview,
        LNotificationsPreviewVisitor,
        LGoalsPreview,
        LGoalsPreviewVisitor,
        LMeetingsPreview,
        LLearningPathsPreview,
        LFeedbackReportsPreview,
        LInstantFeedbackPreview,
        LFileAttachmentsPreview,
        LModal,
        LProfilePicture,
        LUserAccountDirectColleagues,
        LSpace,
        LTeamDashboard,
    },
    data() {
        return {
            isLoading: true,
            showModal: false,
            modalTitle: "",
            showPrivateNoteLock: false,
        };
    },
    computed: {
        ...mapGetters({
            currentUser: "users/current/user",
            userDetails: "users/details/details",
            feedBackDetails: "feedback/feedback/details",
            learningPaths: "learning/library/selfenrolList",
        }),
        isVisitor() {
            return this.$route.params.userId && this.$route.params.userId !== this.currentUser?._id;
        },
        fileAttachments() {
            let details = this.userDetails(this.$route.params.userId);

            return Array.isArray(details?.attachments) ? details.attachments : [];
        },
        visibleUser() {
            return this.isVisitor ? this.userDetails(this.$route.params.userId) : this.currentUser;
        },
        isTeamDashboard() {
            return this.$route.name === "TeamDashboard";
        },
    },
    watch: {
        $route: {
            deep: true,
            immediate: true,
            handler(newRoute) {
                try {
                    if (!this.$route.params.userId) {
                        if (newRoute.name === "FeedbackDetailsOnProfileFallback") {
                            this.$router.replace({
                                path: `/profile/${this.currentUser._id}/feedback/${newRoute.params.id}`,
                                query: newRoute.query,
                                force: true,
                            });
                            return;
                        } else if (
                            newRoute.fullPath.startsWith("/profile") &&
                            this.currentUser?._id
                        ) {
                            this.$router.replace({
                                path: `/profile/${this.currentUser._id}`,
                                query: newRoute.query,
                                force: true,
                            });
                            return;
                        }
                    }
                } catch {
                    // ignore dulication error for router.replace - We don't want to stay on /profile
                }

                this.showModal = newRoute.meta && newRoute.meta.modal;
                this.modalTitle = this.getModalTitleFromRoute(newRoute);
                this.loadData();
            },
        },
    },
    methods: {
        async loadData() {
            if (!this.$route.params.userId) return;

            try {
                await this.$store.dispatch("users/details/get", {
                    payload: { id: this.$route.params.userId },
                });
            } catch {
                // redirect to your own profile on error as visitor
                this.$router.push(`/profile/${this.currentUser._id}`);
            } finally {
                this.isLoading = false;
            }

            // Some boxes need to be hidden completely. Therefore we need to do
            // some initial loading when certain conditions are met and the component
            // itself wasn't rendered yet

            if (this.currentUser?.rights?.L_VIEW) {
                try {
                    await this.$store.dispatch("learning/library/getSelfenrolList");
                } catch {
                    // content remains hidden
                }
            }
        },
        goBack() {
            this.$router.push(`/profile/${this.$route.params.userId ?? this.currentUser._id}`);
        },
        getModalTitleFromRoute() {
            if (!this.$route.params.id) return "Feedback";
            const feedback = this.feedBackDetails(this.$route.params.id);
            return feedback?.title ?? "Feedback";
        },
        updateModalTitle(title, showPrivateNoteLock = false) {
            this.modalTitle = title;
            this.showPrivateNoteLock = showPrivateNoteLock;
        },
    },
};
</script>

<style scoped>
.align-with-content {
    margin-top: 10px;
}
</style>
